import { useEffect, useState } from "react";
import { apiClient } from "../../lib/http/apiClient";

interface Props {
  /** Slug of the pseudo-category row in the `categories` collection, e.g. "news" or "webinars". */
  slug: string;
  /** Display name used only if the row doesn't exist yet and has to be created. */
  name: string;
}

/**
 * Promote / Hide toggle for entities (News, Webinars) that live outside the
 * typed-deal category taxonomy but still need to appear (or not) in the
 * homepage's promoted-categories strip. Backed by the same `categories`
 * collection admin endpoint used for regular categories — PUT upserts the
 * row if it doesn't exist yet.
 */
const CategoryPromotionToggle: React.FC<Props> = ({ slug, name }) => {
  const [isPromoted, setIsPromoted] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get(`/api/v2/categories/${slug}`);
        if (!cancelled) {
          setIsPromoted(Boolean(data?.is_promoted));
          setIsHidden(Boolean(data?.is_hidden));
        }
      } catch {
        // No row yet — defaults (not promoted, not hidden) are fine.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const update = async (field: "is_promoted" | "is_hidden", value: boolean) => {
    setSaving(field);
    try {
      await apiClient.put(`/api/admin/v2/categories/${slug}`, { name, [field]: value });
      if (field === "is_promoted") setIsPromoted(value);
      else setIsHidden(value);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(null);
    }
  };

  if (loading) return null;

  return (
    <div className="flex items-center gap-2 mb-6">
      <button
        onClick={() => void update("is_promoted", !isPromoted)}
        disabled={saving === "is_promoted"}
        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
          isPromoted
            ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
            : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
        }`}
      >
        {isPromoted ? "★ Promoted" : "☆ Promote"}
      </button>
      <button
        onClick={() => void update("is_hidden", !isHidden)}
        disabled={saving === "is_hidden"}
        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
          isHidden
            ? "bg-red-500/20 text-red-400 border border-red-500/30"
            : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
        }`}
      >
        {isHidden ? "Hidden from categories" : "Hide from categories"}
      </button>
    </div>
  );
};

export default CategoryPromotionToggle;
